import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  author?: string;
  geoPosition?: string;
  geoRegion?: string;
  geoPlacename?: string;
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = 'https://public.readdy.ai/ai/img_res/7f75621e5fca0acfb8ab4188110a603a.jpg',
  ogType = 'website',
  keywords = 'El Paso business, start business El Paso, El Paso entrepreneurs, Texas business registration, El Paso small business',
  author = 'ElPasoTexasBusiness.com',
  geoPosition = '31.7619;-106.4850',
  geoRegion = 'US-TX',
  geoPlacename = 'El Paso, Texas',
}: SEOHeadProps) {
  const siteName = 'ElPasoTexasBusiness.com';
  const fullTitle = `${title} | ${siteName}`;
  const baseUrl = 'https://elpasotexasbusiness.com';
  const fullCanonical = `${baseUrl}${canonical}`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Geo meta tags
    updateMetaTag('geo.position', geoPosition);
    updateMetaTag('geo.region', geoRegion);
    updateMetaTag('geo.placename', geoPlacename);
    updateMetaTag('ICBM', geoPosition);

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', fullCanonical, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:site_name', siteName, true);
    updateMetaTag('og:locale', 'en_US', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = fullCanonical;

    // Add last-modified meta tag
    const lastModified = new Date().toISOString();
    updateMetaTag('last-modified', lastModified);

  }, [title, description, canonical, ogImage, ogType, keywords, author, geoPosition, geoRegion, geoPlacename, fullTitle, fullCanonical]);

  return null;
}
